import { useEffect, useRef } from 'react'
import type { FC } from 'react'

import { envs } from '@/envs'
import type { T_AuthCaptchaChallenge } from './authorize-ticket'


const CAPTCHA_SCRIPT_PATH = '/captcha/local/api.js'
const CAPTCHA_SCRIPT_VERSION = '2'

const CAPTCHA_FAILED_MESSAGE = 'Не удалось показать проверку. Попробуйте ещё раз'

type T_RitosCaptcha = {
  render: (
    target: HTMLElement,
    options: {
      challenge: T_AuthCaptchaChallenge
      callback: (token: string) => void
      refresh?: () => Promise<T_AuthCaptchaChallenge>
      errorCallback?: (reason: string) => void
    },
  ) => number
  remove: (id: number) => void
}

type T_Props = {
  challenge: T_AuthCaptchaChallenge
  onToken: (token: string) => void
  onFail: (message?: string) => void
  onRefresh: () => Promise<T_AuthCaptchaChallenge>
}

const captchaScriptUrl = () => {
  const origin = envs.API_DOMAIN || window.location.origin
  const url = new URL(CAPTCHA_SCRIPT_PATH, `${origin}/`)
  url.searchParams.set('v', CAPTCHA_SCRIPT_VERSION)
  return url.href
}

const loadScript = (src: string) => new Promise<void>((resolve, reject) => {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)

  if (existing?.dataset.loaded === 'true') {
    resolve()
    return
  }

  if (existing) {
    existing.addEventListener('load', () => resolve(), { once: true })
    existing.addEventListener('error', () => {
      existing.remove()
      reject(new Error('captcha-script'))
    }, { once: true })
    return
  }

  const script = document.createElement('script')
  script.src = src
  script.async = true
  script.onload = () => {
    script.dataset.loaded = 'true'
    resolve()
  }
  script.onerror = () => {
    script.remove()
    reject(new Error('captcha-script'))
  }
  document.head.appendChild(script)
})

const readRitosCaptcha = () => {
  const host = window as Window & { ritosCaptcha?: T_RitosCaptcha }
  return host.ritosCaptcha ?? null
}

export const AuthCaptcha: FC<T_Props> = ({
  challenge,
  onToken,
  onFail,
  onRefresh,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const onTokenRef = useRef(onToken)
  const onFailRef = useRef(onFail)
  const onRefreshRef = useRef(onRefresh)

  onTokenRef.current = onToken
  onFailRef.current = onFail
  onRefreshRef.current = onRefresh

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let cancelled = false
    let finished = false
    let widgetId: number | null = null

    const removeCaptcha = () => {
      if (widgetId === null) return
      readRitosCaptcha()?.remove(widgetId)
      widgetId = null
      container.replaceChildren()
    }

    const finishFail = () => {
      if (cancelled || finished) return
      finished = true
      removeCaptcha()
      onFailRef.current(CAPTCHA_FAILED_MESSAGE)
    }

    loadScript(captchaScriptUrl())
      .then(() => {
        if (cancelled || finished) return

        const captcha = readRitosCaptcha()
        if (!captcha) {
          finishFail()
          return
        }

        widgetId = captcha.render(container, {
          challenge,
          callback: (token) => {
            if (cancelled || finished || !token) return
            finished = true
            removeCaptcha()
            onTokenRef.current(token)
          },
          refresh: () => onRefreshRef.current(),
        })
      })
      .catch(() => {
        finishFail()
      })

    return () => {
      cancelled = true
      removeCaptcha()
    }
  }, [ challenge ])

  return (
    <div
      id='captcha'
      ref={containerRef}
      style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
    />
  )
}
